// 📄 pages/Mensajes/MensajesHome.tsx
// Bandeja de chat interno v1 — vive en /mensajes (top-level, cruza
// proyectos/organizaciones, ver App.js). Layout de 2 columnas: lista de
// chats a la izquierda, hilo de mensajes a la derecha (mismo patrón que
// cualquier cliente de mensajería simple).
import { useEffect, useMemo, useState } from 'react';
import { MessageCircle, Plus, Send, RefreshCw, Trash2, Users, Building2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { Chat, Mensaje } from '../../types/chat.ts';
import {
  subscribeMisChats, subscribeMensajes, enviarMensaje, eliminarMensaje, marcarChatLeido, tieneNoLeidos
} from '../../services/chatService.ts';
import NuevoChatModal from './NuevoChatModal.tsx';

const nombreDeChat = (chat: Chat, propioUid: string): string => {
  if (chat.tipo === 'grupo') return chat.nombre || 'Grupo sin nombre';
  const otroUid = chat.participantIds.find((id) => id !== propioUid);
  return (otroUid && chat.participantesInfo[otroUid]) || 'Chat';
};

export default function MensajesHome() {
  const { user } = useAuth();
  const [chats, setChats] = useState<Chat[]>([]);
  const [loadingChats, setLoadingChats] = useState(true);
  const [chatSeleccionadoId, setChatSeleccionadoId] = useState<string | null>(null);
  const [mensajes, setMensajes] = useState<Mensaje[]>([]);
  const [loadingMensajes, setLoadingMensajes] = useState(false);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [modalAbierto, setModalAbierto] = useState(false);

  useEffect(() => {
    if (!user) return;
    setLoadingChats(true);
    const unsubscribe = subscribeMisChats(user.uid, (lista) => {
      setChats(lista);
      setLoadingChats(false);
    });
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (!chatSeleccionadoId) { setMensajes([]); return; }
    setLoadingMensajes(true);
    const unsubscribe = subscribeMensajes(chatSeleccionadoId, (lista) => {
      setMensajes(lista);
      setLoadingMensajes(false);
    });
    return () => unsubscribe();
  }, [chatSeleccionadoId]);

  const chatSeleccionado = useMemo(() => chats.find((c) => c.id === chatSeleccionadoId) || null, [chats, chatSeleccionadoId]);

  const abrirChat = (chatId: string) => {
    setChatSeleccionadoId(chatId);
    setModalAbierto(false);
    if (user) marcarChatLeido(chatId, user.uid).catch((err) => console.error('Error marcando el chat como leído:', err));
  };

  const handleEnviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !chatSeleccionadoId || !texto.trim()) return;
    setEnviando(true);
    try {
      await enviarMensaje(chatSeleccionadoId, user.uid, texto);
      setTexto('');
    } catch (err) {
      console.error('Error enviando el mensaje:', err);
    } finally {
      setEnviando(false);
    }
  };

  const handleEliminar = async (mensajeId: string) => {
    if (!chatSeleccionadoId) return;
    try {
      await eliminarMensaje(chatSeleccionadoId, mensajeId);
    } catch (err) {
      console.error('Error eliminando el mensaje:', err);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-white flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-blue-400" /> Mensajes
        </h1>
        <button
          onClick={() => setModalAbierto(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
        >
          <Plus className="w-4 h-4" /> Nuevo chat
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white/5 border border-white/10 rounded-2xl overflow-hidden" style={{ height: '70vh' }}>
        {/* Columna 1: lista de chats */}
        <div className="md:col-span-1 border-r border-white/10 overflow-y-auto">
          {loadingChats ? (
            <div className="p-6 text-center text-gray-400 flex items-center justify-center gap-2 text-sm">
              <RefreshCw className="w-4 h-4 animate-spin" /> Cargando...
            </div>
          ) : chats.length === 0 ? (
            <div className="p-6 text-center text-gray-500 text-sm">
              <MessageCircle className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              Todavía no tenés ningún chat. Empezá uno con "Nuevo chat".
            </div>
          ) : (
            chats.map((chat) => {
              const noLeido = tieneNoLeidos(chat, user.uid);
              return (
                <button
                  key={chat.id}
                  onClick={() => abrirChat(chat.id)}
                  className={`w-full text-left px-4 py-3 border-b border-white/5 transition-colors ${
                    chat.id === chatSeleccionadoId ? 'bg-blue-500/10' : 'hover:bg-white/5'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {chat.tipo === 'grupo' ? <Users className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" /> : <span className="w-3.5 h-3.5 flex-shrink-0" />}
                      <span className={`text-sm truncate ${noLeido ? 'font-semibold text-white' : 'text-gray-300'}`}>
                        {nombreDeChat(chat, user.uid)}
                      </span>
                    </div>
                    {noLeido && <span className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />}
                  </div>
                  <p className="text-xs text-gray-500 truncate mt-0.5 flex items-center gap-1">
                    {chat.contexto === 'organizacion' && <Building2 className="w-3 h-3 flex-shrink-0" />}
                    {chat.ultimoMensaje.autorUid === user.uid ? 'Vos: ' : ''}{chat.ultimoMensaje.texto}
                  </p>
                </button>
              );
            })
          )}
        </div>

        {/* Columna 2-3: hilo de mensajes */}
        <div className="md:col-span-2 flex flex-col">
          {!chatSeleccionado ? (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
              Elegí un chat para ver los mensajes.
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-white/10 flex-shrink-0">
                <h3 className="text-sm font-semibold text-white">{nombreDeChat(chatSeleccionado, user.uid)}</h3>
                {chatSeleccionado.tipo === 'grupo' && (
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    {chatSeleccionado.participantIds.map((id) => chatSeleccionado.participantesInfo[id] || id).join(', ')}
                  </p>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {loadingMensajes ? (
                  <div className="text-center text-gray-400 flex items-center justify-center gap-2 text-sm py-6">
                    <RefreshCw className="w-4 h-4 animate-spin" /> Cargando mensajes...
                  </div>
                ) : (
                  mensajes.map((m) => {
                    const esPropio = m.autorUid === user.uid;
                    return (
                      <div key={m.id} className={`flex ${esPropio ? 'justify-end' : 'justify-start'}`}>
                        <div className={`group max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${
                          m.eliminado ? 'bg-white/5 text-gray-500 italic' : esPropio ? 'bg-blue-600 text-white' : 'bg-white/10 text-gray-100'
                        }`}>
                          {m.eliminado ? 'Mensaje eliminado' : m.texto}
                          {esPropio && !m.eliminado && (
                            <button
                              onClick={() => handleEliminar(m.id)}
                              title="Eliminar mensaje"
                              className="ml-2 inline text-blue-200 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity align-middle"
                            >
                              <Trash2 className="w-3 h-3 inline" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <form onSubmit={handleEnviar} className="p-3 border-t border-white/10 flex items-center gap-2 flex-shrink-0">
                <input
                  type="text"
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder="Escribí un mensaje..."
                  className="flex-1 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                />
                <button
                  type="submit"
                  disabled={enviando || !texto.trim()}
                  className="flex items-center justify-center bg-blue-600 hover:bg-blue-500 disabled:opacity-50 w-9 h-9 rounded-lg text-white transition-all flex-shrink-0"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      {modalAbierto && (
        <NuevoChatModal
          chatsExistentes={chats}
          onClose={() => setModalAbierto(false)}
          onChatListo={abrirChat}
        />
      )}
    </div>
  );
}
